import React, { memo, useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import IdleTimer from 'react-idle-timer';
import SessionTimeoutModal from './SessionTimeoutModal';
import { AuthContext } from '../authentication/AuthContext';
import routes from '../../routes';
import useClearTrace from '../../hooks/useClearTrace';
import {useHistory} from 'react-router-dom';

let countdownInterval;
let timeout;
let disableContinue = false;

const IDLE_DELAY = 1000;
const IDLE_INTERVAL = 1000;
const IDLE_TIMER_DEBOUNCE_INTERVAL = 250;

const clearSessionTimeout = () => {
    clearTimeout(timeout);
};
const clearSessionInterval = () => {
    clearInterval(countdownInterval);
};

const SessionTimeout = ({ timeoutIntervalInSeconds, numberOfIntervals }) => {
    const history = useHistory();
    const { checkIsAuthenticated } = useContext(AuthContext);
    const [timeoutModalOpen, setTimeoutModalOpen] = useState(false);
    const [timeoutCountdown, setTimeoutCountdown] = useState(0);
    const idleTimer = useRef(null);
    const masterTimeoutConst = timeoutIntervalInSeconds * numberOfIntervals;
    const extraSeconds = 5;

    const clearTrace = useClearTrace();

    const handleLogout = () => {
        try {
            setTimeoutModalOpen(false);
            clearSessionInterval();
            clearSessionTimeout();

            // clearTrace probably not necessary, if the logout redirect url set correctly - just to be on safe side
            clearTrace();
            history.push(routes.logoutRedirect.path);
        } catch (err) {
            console.error(err);
        }
    };

    const handleContinue = () => {
        setTimeoutModalOpen(false);
        clearSessionInterval();
        clearSessionTimeout();
    };

    const onActive = () => {
        if (!timeoutModalOpen) {
            clearSessionInterval();
            clearSessionTimeout();
        }
    };

    const calculateCountDown = () => {
        const userSessionTimeStampVar = sessionStorage.getItem('userLoginTimeStamp');
        const userLoginTimeStamp = userSessionTimeStampVar ? Date.parse(userSessionTimeStampVar) : null;
        const currentTimeStamp = new Date();

        if (!userLoginTimeStamp) {
            disableContinue = true;
            return timeoutIntervalInSeconds;
        }

        const currentSessionDuration = Math.floor((currentTimeStamp.getTime() - userLoginTimeStamp) / 1000);

        if (currentSessionDuration > masterTimeoutConst) {
            disableContinue = true;
            return extraSeconds;
        }

        return Math.min(currentSessionDuration, timeoutIntervalInSeconds);
    };

    const onIdle = () => {
        if (checkIsAuthenticated() && !timeoutModalOpen) {
            timeout = setTimeout(() => {
                let countDown = calculateCountDown();
                setTimeoutModalOpen(true);
                setTimeoutCountdown(countDown);
                countdownInterval = setInterval(() => {
                    if (countDown) {
                        setTimeoutCountdown(--countDown);
                    } else {
                        handleLogout();
                    }
                }, IDLE_INTERVAL);
            }, IDLE_DELAY);
        }
    };
    return (
        <>
            <IdleTimer
                ref={idleTimer}
                onActive={onActive}
                onIdle={onIdle}
                debounce={IDLE_TIMER_DEBOUNCE_INTERVAL}
                timeout={timeoutIntervalInSeconds * 1000}
            />
            <SessionTimeoutModal
                countdown={timeoutCountdown}
                onContinue={handleContinue}
                onLogout={() => handleLogout()}
                open={timeoutModalOpen}
                disableContinue={disableContinue}
                data-testid={'SessionTimeoutModal'}
            />
        </>
    );
};

SessionTimeout.displayName = 'SessionTimeout';

SessionTimeout.propTypes = {
    timeoutIntervalInSeconds: PropTypes.number.isRequired,
    numberOfIntervals: PropTypes.number.isRequired,
};

export default memo(SessionTimeout);
